/**
 * Section Nav Block
 *
 * Toggles visibility of page sections based on user selection.
 * Persists state in a cookie.
 */
export default function () {
	const nav = document.querySelector('.section-nav');
	if (!nav) return;

	const buttons = nav.querySelectorAll('.section-nav__button');
	const list = nav.querySelector('.section-nav__list');
	const COOKIE_NAME = 'apta_section_nav';
	const COOKIE_DAYS = 365;

	// --- Cookie helpers ---
	function getCookie(name) {
		const nameEQ = name + '=';
		const ca = document.cookie.split(';');
		for (let i = 0; i < ca.length; i++) {
			let c = ca[i].trim();
			if (c.indexOf(nameEQ) === 0) {
				return c.substring(nameEQ.length);
			}
		}
		return null;
	}

	function setCookie(name, value, days) {
		let expires = '';
		if (days) {
			const date = new Date();
			date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
			expires = '; expires=' + date.toUTCString();
		}
		document.cookie = name + '=' + value + expires + '; path=/';
	}

	// --- State management ---
	function getEnabledFromCookie() {
		const raw = getCookie(COOKIE_NAME);
		if (!raw) return null;
		try {
			return JSON.parse(decodeURIComponent(raw));
		} catch (e) {
			return null;
		}
	}

	function getDefaults() {
		const enabled = [];
		buttons.forEach(btn => {
			if (btn.dataset.defaultEnabled === 'true') {
				enabled.push(btn.dataset.target);
			}
		});
		return enabled;
	}

	function saveToCookie(enabledList) {
		setCookie(COOKIE_NAME, encodeURIComponent(JSON.stringify(enabledList)), COOKIE_DAYS);
	}

	// --- Apply state ---
	function applyState(enabledList) {
		buttons.forEach(btn => {
			const target = btn.dataset.target;
			const isEnabled = enabledList.includes(target);

			btn.classList.toggle('--active', isEnabled);
			btn.setAttribute('aria-pressed', isEnabled ? 'true' : 'false');

			const section = document.querySelector(target);
			if (section) {
				section.style.display = isEnabled ? '' : 'none';
			}
		});
	}

	// --- Locked (first) button ---
	const lockedTargets = Array.from(nav.querySelectorAll('.section-nav__button[data-locked="true"]'))
		.map(btn => btn.dataset.target);

	// --- Initialize ---
	let enabledList = getEnabledFromCookie();
	if (!enabledList) {
		enabledList = getDefaults();
	}
	// Ensure locked targets are always included
	lockedTargets.forEach(t => {
		if (!enabledList.includes(t)) enabledList.push(t);
	});
	applyState(enabledList);

	// --- Click handler ---
	buttons.forEach(btn => {
		btn.addEventListener('click', () => {
			if (btn.dataset.locked === 'true') return;
			const target = btn.dataset.target;
			const idx = enabledList.indexOf(target);
			const wasEnabled = idx > -1;

			if (wasEnabled) {
				enabledList.splice(idx, 1);
			} else {
				enabledList.push(target);
			}

			applyState(enabledList);
			saveToCookie(enabledList);

			if (!wasEnabled) {
				const section = document.querySelector(target);
				if (section) {
					// Notify components that initialized while hidden (e.g. Slick
					// carousels with zero-width slides) so they can recalc.
					section.dispatchEvent(new CustomEvent('apta:section-shown', { bubbles: true }));
					setTimeout(() => {
						section.scrollIntoView({ behavior: 'smooth', block: 'start' });
					}, 300);
				}
			}
		});
	});

	// --- Mobile drag-to-scroll ---
	if (window.innerWidth < 800 && list) {
		list.classList.add('--grabbable');

		let isDown = false;
		let startX;
		let scrollLeft;

		list.addEventListener('mousedown', e => {
			isDown = true;
			list.classList.add('--dragging');
			startX = e.pageX - list.offsetLeft;
			scrollLeft = list.scrollLeft;
		});

		list.addEventListener('mouseleave', () => {
			isDown = false;
			list.classList.remove('--dragging');
		});

		list.addEventListener('mouseup', () => {
			isDown = false;
			list.classList.remove('--dragging');
		});

		list.addEventListener('mousemove', e => {
			if (!isDown) return;
			e.preventDefault();
			const x = e.pageX - list.offsetLeft;
			const walk = (x - startX) * 1.5;
			list.scrollLeft = scrollLeft - walk;
		});
	}
}
